import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import GlitchText from '../components/ui/GlitchText';
import { medicalFieldIcons } from '../components/icons/XionIcons';
import { medicalFields } from '../data/medicalFields';
import { useMotion } from '../context/MotionContext';
import { trackPageView } from '../utils/analytics';

const FIELD_ICONS = medicalFieldIcons;

const MedicalFieldsLandingPage = () => {
  const { prefersReducedMotion } = useMotion();

  useEffect(() => {
    trackPageView('MedicalFields');
  }, []);

  return (
    <>
      <SEOHead
        title="Medical Fields"
        description="XION endoscopy solutions by medical specialty — ENT, Gynecology, Laparoscopy and Urology. Integrated systems, endoscopes and imaging for every clinical field, distributed in the Philippines by FMSInc."
        path="/medical-fields"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-hud" data-testid="medical-fields-landing">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Header */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// Solutions by Specialty</p>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {prefersReducedMotion ? 'Medical Fields' : <GlitchText text="Medical Fields" />}
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
              One modular XION platform, configured for the way you work. Explore the systems,
              endoscopes and imaging technology matched to each clinical specialty.
            </p>
          </motion.div>

          {/* Field cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {medicalFields.map((field, idx) => {
              const Icon = FIELD_ICONS[field.slug] || FIELD_ICONS.ent;
              return (
                <motion.div
                  key={field.slug}
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <Link
                    to={`/medical-fields/${field.slug}`}
                    data-testid={`medical-fields-card-${field.slug}`}
                    className="hud-card corner-brackets rounded-lg overflow-hidden flex flex-col sm:flex-row group hover:border-[#00FFD1]/50 transition-colors h-full"
                  >
                    <div className="sm:w-44 shrink-0 bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-b sm:border-b-0 sm:border-r border-white/10 flex items-center justify-center p-6">
                      <img
                        src={field.heroImage}
                        alt={`XION ${field.fullName}`}
                        className="w-full h-40 sm:h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={20} className="text-[#00FFD1]" />
                        <p className="terminal-label text-[#00FFD1] text-xs">{field.kicker}</p>
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-1 group-hover:text-[#00FFD1] transition-colors">
                        {field.name}
                      </h2>
                      <p className="text-[#00FFD1] text-sm mb-3">{field.tagline}</p>
                      <p className="text-white/60 text-sm leading-relaxed line-clamp-3 mb-4">{field.intro[0]}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#00FFD1] text-sm font-mono opacity-80 group-hover:opacity-100 transition-opacity">
                        Explore {field.name} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-3">Not sure which configuration fits?</h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              FMSInc., the exclusive XION distributor in the Philippines, will help you specify the
              right system for your department.
            </p>
            <Link to="/contact-ph" className="btn-primary inline-flex" data-testid="medical-fields-cta">
              Talk to FMSInc.
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default MedicalFieldsLandingPage;
